import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCode } from 'react-qr-code';
import { useRef } from 'react';
import Navbar from '../navbar/Navbar.js';
import { showToast } from '../Notify.js';
import { api } from '../../api/index';
import './pollingOutcome.css';

const PollingOutcome = () => {
  const { pollId, rewardCode, votedOptionId } = useParams();
  const navigate = useNavigate();
  const qrRef = useRef(null);

  const [pollingData, setPollingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRewardSection, setShowRewardSection] = useState(false);

  useEffect(() => {
    // Prevent back navigation
    window.history.pushState(null, '', window.location.href);
    const handleBackButton = (e) => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', handleBackButton);
    return () => window.removeEventListener('popstate', handleBackButton);
  }, []);

  useEffect(() => {
    const fetchPollingResults = async () => {
      try {
        if (!pollId) {
          setError('Invalid polling ID');
          setLoading(false);
          return;
        }

        // Use fetch to get polling analytics
        const response = await fetch(`${api}/polls/poll-responses/${pollId}/analytics`, {
          signal: AbortSignal.timeout(10000)
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch polling results`);
        }

        const result = await response.json();
        console.log('Polling results:', result);

        if (result.data) {
          setPollingData(result.data);
        } else {
          setError('Failed to load polling results');
        }
      } catch (err) {
        console.error('Error fetching polling results:', err);
        setError(err.message || 'Failed to load polling results');
      } finally {
        setLoading(false);
      }
    };

    fetchPollingResults();
  }, [pollId]);

  // Color palette for progress bars
  const progressBarColors = [
    '#3b82f6', // blue
    '#ef4444', // red
    '#60a5fa', // light blue
    '#10b981', // green
    '#8b5cf6', // purple
    '#f97316'  // orange
  ];

  const getProgressBarColor = (index) => {
    return progressBarColors[index % progressBarColors.length];
  };

  // Generate reward QR code data
  const generateRewardQRData = () => {
    if (!rewardCode) return `POLL-${pollId}`;
    return `POLL-${pollId}-${rewardCode}`;
  };

  // Download QR code as PNG
  const downloadQRCode = () => {
    if (!qrRef.current) return;

    const svg = qrRef.current.querySelector('svg');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Get SVG dimensions
    const size = 256;
    canvas.width = size;
    canvas.height = size;

    // Convert SVG to image
    const svgString = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0);

      // Download
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `polling-reward-${pollId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast('QR code downloaded successfully', 'success');
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgString);
  };

  // Share QR code
  const shareQRCode = async () => {
    try {
      const qrText = generateRewardQRData();
      const shareText = `I just voted in a polling event! 🎉\n\nReward Code: ${qrText}\n\nScan the QR code to claim your reward or share it with friends!`;

      if (navigator.share) {
        await navigator.share({
          title: 'Polling Reward',
          text: shareText,
        });
        showToast('Shared successfully', 'success');
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(shareText);
        showToast('Reward code copied to clipboard', 'success');
      }
    } catch (err) {
      console.error('Error sharing:', err);
      if (err.name !== 'AbortError') {
        showToast('Failed to share', 'error');
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="outcome-container">
          <div className="outcome-loading">
            <div className="spinner"></div>
            <p>Loading polling results...</p>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error || !pollingData) {
    return (
      <>
        <Navbar />
        <div className="outcome-container">
          <div className="outcome-error">
            <h2>⚠️ Error</h2>
            <p>{error || 'Unable to load polling results'}</p>
            <button
              onClick={() => navigate('/')}
              className="outcome-action-btn outcome-home-btn"
            >
              Go Home
            </button>
          </div>
        </div>
      </>
    );
  }

  const poll = pollingData.poll || {};
  const options = pollingData.options || [];
  const totalResponses = pollingData.total_responses || 0;

  // Get highest percentage for winner badges
  const maxPercentage = options.length > 0
    ? Math.max(...options.map(opt => parseFloat(opt.percentage)))
    : 0;

  const pollTitle = poll.title || 'Polling Results';

  return (
    <>
      <Navbar />
      <div className="outcome-container">
        <div className="outcome-content">
          {/* Results Card */}
          <div className="outcome-card">
            {/* Header */}
            <div className="outcome-header">
              <h1 className="outcome-title">{pollTitle}</h1>
              <p className="outcome-stats">
                Total Votes: <strong>{totalResponses}</strong>
              </p>
            </div>

            {/* Results */}
            <div className="outcome-results">
              {options.length > 0 ? (
                options
                  .sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage))
                  .map((option, index) => (
                    <div
                      key={option.option_id}
                      className={`outcome-option ${votedOptionId && parseInt(votedOptionId) === option.option_id ? 'voted' : ''}`}
                    >
                      {/* Icon */}
                      {option.icon_url && (
                        <div className="outcome-option-icon">
                          <img
                            src={option.icon_url}
                            alt={option.label}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      {/* Label and Metrics */}
                      <div className="outcome-option-details">
                        <div className="outcome-option-header">
                          <span className="outcome-option-label">{option.label}</span>
                          <div className="outcome-badges">
                            {votedOptionId && parseInt(votedOptionId) === option.option_id && (
                              <span className="outcome-voted-badge">✓ YOUR VOTE</span>
                            )}
                            {parseFloat(option.percentage) === maxPercentage && (
                              <span className="outcome-winner-badge">★ WINNING</span>
                            )}
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="outcome-progress-container">
                          <div
                            className="outcome-progress-bar"
                            style={{
                              width: `${Math.round(option.percentage)}%`,
                              backgroundColor: getProgressBarColor(index)
                            }}
                          ></div>
                        </div>

                        {/* Percentage and Count */}
                        <div className="outcome-metrics">
                          <span className="outcome-percentage">
                            {Math.round(option.percentage)}%
                          </span>
                          <span className="outcome-vote-count">
                            {option.response_count} {option.response_count === 1 ? 'vote' : 'votes'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
              ) : (
                <p className="outcome-no-data">No polling data available</p>
              )}
            </div>
          </div>

          {/* Reward QR Code Section - Button Only */}
          <div className="outcome-reward-section">
            {/* Toggle Button */}
            <button
              onClick={() => setShowRewardSection(true)}
              className="outcome-reward-toggle-btn"
              title="Show reward code"
            >
              <span className="toggle-icon">🎁</span>
              <span className="toggle-label">Claim Your Reward</span>
            </button>
          </div>

          {/* Reward Modal Overlay */}
          {showRewardSection && (
            <div className="outcome-modal-overlay" onClick={() => setShowRewardSection(false)}>
              <div className="outcome-modal" onClick={(e) => e.stopPropagation()}>
                {/* Close Button */}
                <button
                  onClick={() => setShowRewardSection(false)}
                  className="outcome-modal-close"
                  title="Close"
                >
                  ✕
                </button>

                {/* Modal Header */}
                <div className="outcome-modal-header">
                  <h2>🎁 Your Reward Code</h2>
                  <p>Scan or share this QR code to claim your reward!</p>
                </div>

                {/* QR Code Container */}
                <div className="outcome-qr-wrapper" ref={qrRef}>
                  <div className="outcome-qr-container">
                    <QRCode
                      value={generateRewardQRData()}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="outcome-action-buttons">
                  <button
                    onClick={downloadQRCode}
                    className="outcome-action-btn outcome-download-btn"
                    title="Download QR code"
                  >
                    ⬇️ Download QR
                  </button>
                  <button
                    onClick={shareQRCode}
                    className="outcome-action-btn outcome-share-btn"
                    title="Share QR code"
                  >
                    📤 Share
                  </button>
                </div>

                {/* Validity Notice */}
                <p className="outcome-validity-notice">
                  ℹ️ This reward code is valid for a limited time. Save it for later use!
                </p>
              </div>
            </div>
          )}

         
        </div>
      </div>
    </>
  );
};

export default PollingOutcome;
